import { useMemo } from 'react';

import type { AssetInfo, ProductionState } from '@/hooks/useProduction';
import { VIEW_SUFFIX_RE, candidateParent, normKey } from '@/lib/assetKeys';

/** Detail views are shown in a fixed, readable order under the model sheet. */
const VIEW_ORDER = ['front', 'faceclose', 'hairdetail', 'threequarter', 'three_quarter', '3q', 'profile', 'side'];

/**
 * Collapse variant twins of ONE entity (e.g. "Academic Office, Reenactment" vs
 * "ACADEMIC_OFFICE,_REENACTMENT") that the design-JSON + GCS-listing union can
 * briefly emit for the same place/person during a live run: same normKey → one card,
 * keeping the entry that carries an authored LOC id and a readable (spaced) name.
 * Mirrors the engine's canonical-entity dedup at the render site.
 */
function dedupeByKey(arr: AssetInfo[]): AssetInfo[] {
  const score = (x: AssetInfo) => (x.location_id ? 2 : 0) + (/\s/.test(x.shot_id) ? 1 : 0);
  const best = new Map<string, AssetInfo>();
  for (const a of arr) {
    const k = normKey(a.shot_id);
    const cur = best.get(k);
    if (!cur || score(a) > score(cur)) best.set(k, a);
  }
  return Array.from(best.values());
}

/**
 * A candidate is "decided" (the pick has been made) once a promoted, non-candidate
 * tile with the same name exists — then it greys out and loses its ITERATING chip.
 */
const decidedKeys = (arr: AssetInfo[]) => new Set(arr.filter((a) => !candidateParent(a.shot_id)).map((a) => normKey(a.shot_id)));

/** Sort locations by their authored LOC_nnn number; unnumbered ones sort last by name. */
const locNum = (l: AssetInfo) => {
  const m = /(\d+)/.exec(l.location_id || '');
  return m ? parseInt(m[1], 10) : 9999;
};

/** Derives the character / location / prop boards from the raw asset list. */
export function useDesignAssets(assets: ProductionState['assets'], breakdown: unknown) {
  return useMemo(() => {
    // Each character has a 3-up model sheet (e.g. HOST) PLUS separate per-view
    // reference portraits (HOST_front/_profile/…) fed to the keyframe generators.
    // Those views are NOT separate characters — show only the model sheets.
    const allCharacterAssets = assets.filter((a) => a.type === 'character');
    const characters = dedupeByKey(allCharacterAssets.filter((a) => !VIEW_SUFFIX_RE.test(a.shot_id)));

    const viewsFor = (charId: string) => {
      const base = normKey(charId);
      return allCharacterAssets
        .filter((a) => {
          const m = VIEW_SUFFIX_RE.exec(a.shot_id);
          return !!m && normKey(a.shot_id.slice(0, m.index)) === base;
        })
        .sort((a, b) => {
          const tag = (x: string) => (VIEW_SUFFIX_RE.exec(x)?.[1] || '').toLowerCase();
          return VIEW_ORDER.indexOf(tag(a.shot_id)) - VIEW_ORDER.indexOf(tag(b.shot_id));
        });
    };

    const locations = dedupeByKey(assets.filter((a) => a.type === 'location')).sort(
      (a, b) => locNum(a) - locNum(b) || a.shot_id.localeCompare(b.shot_id, undefined, { numeric: true }),
    );
    const props = assets.filter((a) => a.type === 'prop').sort((a, b) => a.shot_id.localeCompare(b.shot_id));

    // Locations named in the script breakdown that have no design yet — shown as
    // "queued" placeholders so the board reflects the full location list up front.
    const haveLocs = new Set(locations.map((l) => normKey(l.shot_id)));
    const plannedLocs: string[] = [];
    if (breakdown && typeof breakdown === 'object' && 'scenes' in (breakdown as Record<string, unknown>)) {
      const seen = new Set<string>();
      for (const sc of (breakdown as { scenes: { location?: string }[] }).scenes || []) {
        const loc = (sc.location || '').trim();
        if (loc && !seen.has(normKey(loc)) && !haveLocs.has(normKey(loc))) {
          seen.add(normKey(loc));
          plannedLocs.push(loc);
        }
      }
    }

    return {
      characters,
      viewsFor,
      locations,
      props,
      plannedLocs,
      decidedChar: decidedKeys(characters),
      decidedLoc: decidedKeys(locations),
      decidedProp: decidedKeys(props),
    };
  }, [assets, breakdown]);
}
